import { Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useCart } from "@/lib/cart";
import { useAuth } from "@/lib/auth";
import { useWishlist } from "@/lib/wishlist";
import { Menu, ShoppingBag, Heart, X, User } from "lucide-react";
import { Monogram } from "@/components/site/Monogram";

export function Header() {
  const { count } = useCart();
  const { count: wishCount } = useWishlist();
  const { user, signOut } = useAuth();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
    <header
      className={`fixed inset-x-0 top-0 z-40 transition-all duration-500 bg-background/95 backdrop-blur-md ${
        scrolled ? "border-b border-border shadow-[0_1px_0_0_rgba(0,0,0,0.04)]" : "border-b border-border/60"
      }`}
    >
      <div className="container-luxe relative flex h-20 items-center justify-between gap-4 md:h-24">
        <button
          aria-label="Menu"
          className="p-2 -ml-2"
          onClick={() => setOpen(true)}
        >
          <Menu className="h-6 w-6" />
        </button>

        <nav className="hidden" aria-hidden="true">
          <Link to="/new-arrivals" preload="intent" className="underline-grow text-cognac">New</Link>
          <Link to="/women" preload="intent" className="underline-grow">Women</Link>
          <Link to="/men" preload="intent" className="underline-grow">Men</Link>
          <Link to="/unisex" preload="intent" className="underline-grow">Unisex</Link>
          <Link to="/accessories" preload="intent" className="underline-grow">Accessories</Link>
          <Link to="/riders" preload="intent" className="underline-grow">Riders</Link>
          <Link to="/journal" preload="intent" className="underline-grow">Journal</Link>
        </nav>

        <Link
          to="/"
          className="absolute left-1/2 flex -translate-x-1/2 items-center gap-3"
        >
          <Monogram
            className="h-8 w-8 shrink-0 text-cognac md:h-9 md:w-9 xl:h-10 xl:w-10"
            strokeWidth={6}
            title="KAKAIYS monogram"
            eager
          />
          <span className="hidden font-serif text-xl tracking-[0.32em] text-ink sm:inline sm:text-2xl xl:text-[30px] xl:tracking-[0.38em]">
            KAKAIYS
          </span>
        </Link>

        <div className="flex items-center gap-1 md:gap-2">
          {user ? (
            <button
              aria-label="Sign out"
              onClick={() => signOut()}
              className="hidden p-2 text-[11px] uppercase tracking-[0.2em] sm:inline-flex"
              title={user.email ?? ""}
            >
              <User className="h-[18px] w-[18px]" />
            </button>
          ) : (
            <Link to="/auth" search={{}} aria-label="Sign in" className="hidden p-2 sm:inline-flex">
              <User className="h-[18px] w-[18px]" />
            </Link>
          )}
          <Link to="/wishlist" aria-label="Wishlist" className="relative p-2">
            <Heart className="h-[18px] w-[18px]" />
            {wishCount > 0 && (
              <span className="absolute -right-0.5 -top-0.5 h-4 min-w-4 rounded-full bg-foreground text-background text-[10px] font-medium flex items-center justify-center px-1">
                {wishCount}
              </span>
            )}
          </Link>
          <Link to="/cart" aria-label="Cart" className="relative p-2">
            <ShoppingBag className="h-[18px] w-[18px]" />
            {count > 0 && (
              <span className="absolute -right-0.5 -top-0.5 h-4 min-w-4 rounded-full bg-foreground text-background text-[10px] font-medium flex items-center justify-center px-1">
                {count}
              </span>
            )}
          </Link>
        </div>
      </div>
    </header>

    {/* Mobile drawer — sibling of <header> to avoid backdrop-filter context */}
    <div
      className={`fixed inset-0 z-[60] transition-opacity ${
        open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
      }`}
    >
      <div className="absolute inset-0 bg-ink/40" onClick={() => setOpen(false)} />
      <div
        className={`absolute left-0 top-0 h-full w-[78%] max-w-sm text-foreground p-6 transition-transform duration-500 ${
          open ? "translate-x-0" : "-translate-x-full"
        }`}
        style={{ backgroundColor: "var(--bone)" }}
      >
        <div className="flex items-center justify-between mb-10">
          <span className="font-serif text-xl tracking-[0.3em]">KAKAIYS</span>
          <button onClick={() => setOpen(false)} aria-label="Close">
            <X className="h-5 w-5" />
          </button>
        </div>
        <nav className="flex flex-col gap-5 text-lg font-serif">
          <Link to="/new-arrivals" onClick={() => setOpen(false)} className="text-cognac">New Arrivals</Link>
          <Link to="/women" onClick={() => setOpen(false)}>Women</Link>
          <Link to="/men" onClick={() => setOpen(false)}>Men</Link>
          <Link to="/unisex" onClick={() => setOpen(false)}>Unisex</Link>
          <Link to="/accessories" onClick={() => setOpen(false)}>Accessories</Link>
          <Link to="/riders" onClick={() => setOpen(false)}>Riders</Link>
          <Link to="/our-story" onClick={() => setOpen(false)}>Our Story</Link>
          <Link to="/journal" onClick={() => setOpen(false)}>Journal</Link>
          <Link to="/reviews" onClick={() => setOpen(false)}>Reviews</Link>
          <Link to="/wishlist" onClick={() => setOpen(false)}>Wishlist ({wishCount})</Link>
          <Link to="/cart" onClick={() => setOpen(false)}>Cart ({count})</Link>
          <div className="hairline my-2" />
          {user ? (
            <>
              <button
                onClick={() => {
                  signOut();
                  setOpen(false);
                }}
                className="text-left"
              >
                Sign out
              </button>
            </>
          ) : (
            <Link to="/auth" search={{}} onClick={() => setOpen(false)}>Sign in</Link>
          )}
        </nav>
      </div>
    </div>
    </>
  );
}
